import { forwardRef, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { utils } from '@grammarly/badger-ui'

import { TypeComponentSelect } from '../../lib/types/component-select'
import { ContentProps } from '../renderer'
import { ErrorText, FormControl, Select, Label } from '../ui-lib/form'
import { HelperText } from '../ui-lib/form'
import { InputError, useError } from './hooks'
import { conditionalCheck } from './conditional-check'
const { mergeRefs } = utils

interface SelectProps extends ContentProps<TypeComponentSelect> {
  labelClassName?: string
  helperTextClassName?: string
}

export const SelectOption = forwardRef(
  ({ structure, className, labelClassName, helperTextClassName }: SelectProps, ref) => {
    const {
      fields: { name, helperText, label, isRequired, options, conditionalDisplay, internalName },
    } = structure

    const { register, watch, resetField } = useFormContext()

    const helperTextId = `${name}-helper-text`

    let isConditionalMet = true

    if (conditionalDisplay) {
      const conditionalValues = conditionalDisplay.fields.value
      const conditionalOperator = conditionalDisplay.fields.logicOperator
      const conditionalWatch = watch(conditionalDisplay.fields.formFieldToWatch.fields.name)
      isConditionalMet = conditionalCheck(conditionalOperator, conditionalWatch, conditionalValues)
    }

    const { ref: registerRef, ...registerRest } = register(name, {
      required: { value: isConditionalMet && !!isRequired, message: InputError.SelectRequired },
    })

    const error = useError(name)

    useEffect(() => {
      if (!isConditionalMet) {
        resetField(name)
      }
    }, [isConditionalMet, name, resetField])

    if (!isConditionalMet) {
      return null
    }

    return (
      <FormControl name={name} isRequired={isRequired} isInvalid={!!error} error={error}>
        {label && <Label className={labelClassName}>{label}</Label>}
        {helperText && (
          <HelperText id={helperTextId} className={helperTextClassName}>
            {helperText}
          </HelperText>
        )}
        <Select
          ref={mergeRefs(registerRef, ref)}
          className={className}
          ariaDescribedBy={helperTextId}
          options={options}
          data-component-id={internalName}
          {...registerRest}
        />
        <ErrorText />
      </FormControl>
    )
  }
)
